/**
 * The figures behind the week's chart footer, the hours bar and the fair spread (issue #207, prototype
 * vaktplan-real.prototype.html renderWeekCharts and fairOf). Pure: the screens draw what these return.
 */
import type { ApiShift } from '@/api/data';
import type { CostDay } from '@/api/labour';
import { oslo, osloISO } from '@/rules';
import type { NeedProfile } from '@/shell/local';
import { addDays, dow } from '@timefly/core';
import { pieces, workOf } from './staffing';

/** One hour of a day: the need, the person-hours on shift in it, and the fewest people at once. */
export interface HourCover {
  h: number;
  need: number;
  on: number;
  low: number;
}

/** Each hour from h0 to h1 (h1 not included) on `date`, counted the way hourGaps counts a gap. */
export function hourCover(need: NeedProfile, date: string, shifts: ApiShift[], h0: number, h1: number) {
  const work = workOf(shifts);
  const day = need[dow(date)] ?? {};
  const dayEnd = Date.parse(osloISO(addDays(date, 1), '00:00'));
  const out: HourCover[] = [];
  for (let h = h0; h < h1; h++) {
    const a = Date.parse(osloISO(date, `${String(h).padStart(2, '0')}:00`));
    const b = Math.min(a + 36e5, dayEnd);
    let on = 0;
    let low = Number.POSITIVE_INFINITY;
    for (const p of pieces(work, a, b)) {
      on += (p.have * (p.b - p.a)) / 36e5;
      low = Math.min(low, p.have);
    }
    out.push({ h, need: Number(day[h]) || 0, on, low: Number.isFinite(low) ? low : 0 });
  }
  return out;
}

/**
 * The coverage strip's state (prototype, owner 2026-10-27): short by a person all through the hour, short part of
 * it, enough, or at least one more than the need and half again over it.
 */
export function coverState(z: HourCover): 'none' | 'short' | 'thin' | 'ok' | 'over' {
  if (!z.need && !z.on) return 'none';
  if (z.low < z.need) return z.need - z.on >= 1 - 1e-6 ? 'short' : 'thin';
  if (z.on - z.need >= 1 && (z.need ? z.on / z.need : 2) > 1.5) return 'over';
  return 'ok';
}

/** The hours to draw: from the first hour anyone is needed or on shift in the days to the last; 6 to 22 when none. */
export function hourRange(need: NeedProfile, days: string[], shifts: ApiShift[]): [number, number] {
  let a = 24;
  let b = 0;
  for (const d of days)
    for (const [h, n] of Object.entries(need[dow(d)] ?? {}))
      if (Number(n) > 0) {
        a = Math.min(a, Number(h));
        b = Math.max(b, Number(h) + 1);
      }
  for (const s of shifts) {
    const st = oslo(s.start_time);
    const en = oslo(s.end_time);
    if (s.user_id == null || !st || !en || !days.includes(st.date)) continue;
    a = Math.min(a, Number(st.time.slice(0, 2)));
    b = Math.max(
      b,
      en.date > st.date ? 24 : Math.ceil(Number(en.time.slice(0, 2)) + Number(en.time.slice(3)) / 60),
    );
  }
  return a < b ? [a, b] : [6, 22];
}

/** The side of a day's cost the bottom row counts (MoneyRow dayFigure): what was clocked on a worked day, else the plan. */
export function costSide(c: CostDay | undefined, past: boolean) {
  if (!c) return null;
  return past && c.actual.shifts > 0 ? c.actual : c.planned;
}

/**
 * Evening shifts (ending 21:00 or later, or past midnight) and weekend shifts (starting Saturday or Sunday) per
 * person, among assigned shifts starting from `from` to `to` (Oslo dates, both included). Owner 2026-10-27.
 */
export function fairSpread(shifts: ApiShift[], from: string, to: string) {
  const by = new Map<number, { eve: number; wkd: number }>();
  const seen = new Set<number>();
  for (const s of shifts) {
    const st = oslo(s.start_time);
    const en = oslo(s.end_time);
    if (s.user_id == null || !st || !en || st.date < from || st.date > to || seen.has(s.id)) continue;
    seen.add(s.id);
    const f = by.get(s.user_id) ?? { eve: 0, wkd: 0 };
    if (en.date > st.date || en.time >= '21:00') f.eve++;
    if (dow(st.date) >= 5) f.wkd++;
    by.set(s.user_id, f);
  }
  return by;
}

/** Orange when at least half again and 2 shifts above the team's average, blue at most 40 % of it (prototype). */
export function fairMark(v: number, avg: number): 'hi' | 'lo' | '' {
  if (v >= avg * 1.5 && v - avg >= 2) return 'hi';
  if (avg >= 2 && v <= avg * 0.4) return 'lo';
  return '';
}

/**
 * Labour cost against the month's budget (prototype burnHTML): the running total by day, what was clocked up to
 * yesterday and the plan from today, so the last point is the forecast for the month's end.
 */
export function burn(days: string[], today: string, cost: Map<string, CostDay>) {
  let sum = 0;
  let sofar = 0;
  const cum = days.map((d) => {
    sum += costSide(cost.get(d), d < today)?.cost ?? 0;
    if (d < today) sofar = sum;
    return sum;
  });
  return { cum, sofar, forecast: sum, done: days.filter((d) => d < today).length };
}
